import { useState, useEffect, useMemo, useRef, useCallback, type FormEvent } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { useCart } from '../context/CartContext';
import { useMenu } from '../context/MenuContext';
import { submitOrder, fetchStripeConfig, createPaymentIntent, fetchOnlineStatus, checkAddress } from '../api';
import { PaymentMethodPicker } from './PaymentMethodPicker';
import { PaymentForm } from './PaymentForm';
import { useI18n } from '../i18n';
import type { OrderPayload, DeliveryZone, DeliveryZoneFeeItem, OnlineWorkingHoursEntry } from '../types';

// Scheduled (delayed) order parameters. Mirrors the backend's
// DELAYED_MIN_LEAD_MINUTES — keep in sync.
const SCHEDULE_LEAD_MINUTES = 60;
const SCHEDULE_SLOT_MINUTES = 15;
const SCHEDULE_HORIZON_DAYS = 14;

interface ScheduleSlots {
  /** Available dates as YYYY-MM-DD strings, ordered. */
  dates: string[];
  /** date → list of HH:mm slot strings, ordered. */
  byDate: Record<string, string[]>;
}

/** Generate selectable delivery slots from the online working-hours
 *  schedule. Uses the customer's local clock (assumed to be Europe/Zagreb
 *  for HalloChef's audience); the backend re-validates in the configured
 *  business timezone so a foreign visitor mis-picking a slot fails cleanly.
 */
function buildScheduleSlots(schedule: OnlineWorkingHoursEntry[] | undefined): ScheduleSlots {
  const dates: string[] = [];
  const byDate: Record<string, string[]> = {};
  if (!schedule || schedule.length === 0) return { dates, byDate };

  // Plain object instead of global `Map` — the @vis.gl Map component is
  // already imported as `Map` in this file and shadows the builtin.
  const byDay: Record<number, OnlineWorkingHoursEntry> = {};
  for (const entry of schedule) {
    if (entry.active) byDay[entry.day] = entry;
  }
  if (Object.keys(byDay).length === 0) return { dates, byDate };

  const now = new Date();
  const earliest = new Date(now.getTime() + SCHEDULE_LEAD_MINUTES * 60_000);

  for (let offset = 0; offset < SCHEDULE_HORIZON_DAYS; offset++) {
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    // JS Date.getDay() returns 0=Sunday..6=Saturday; backend's stored
    // schedule uses 0=Monday..6=Sunday (Python weekday).
    const weekday = (day.getDay() + 6) % 7;
    const entry = byDay[weekday];
    if (!entry) continue;
    const [openH, openM] = entry.open.split(':').map((x: string) => parseInt(x, 10));
    const [closeH, closeM] = entry.close.split(':').map((x: string) => parseInt(x, 10));
    if (!Number.isFinite(openH) || !Number.isFinite(closeH)) continue;
    const openMins = openH * 60 + openM;
    const closeMins = closeH * 60 + closeM;
    if (closeMins <= openMins) continue;

    const slots: string[] = [];
    for (let m = openMins; m < closeMins; m += SCHEDULE_SLOT_MINUTES) {
      const slot = new Date(day.getFullYear(), day.getMonth(), day.getDate(), Math.floor(m / 60), m % 60);
      if (slot < earliest) continue;
      const hh = String(Math.floor(m / 60)).padStart(2, '0');
      const mm = String(m % 60).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
    }
    if (slots.length === 0) continue;
    const isoDate = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    dates.push(isoDate);
    byDate[isoDate] = slots;
  }
  return { dates, byDate };
}

/** Format YYYY-MM-DD for human display. */
function formatDateLabel(iso: string, lang: string): string {
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(lang === 'hr' ? 'hr-HR' : 'en-GB', {
    weekday: 'short', day: '2-digit', month: '2-digit',
  });
}

interface MatchedZoneSummary {
  id: number;
  name: string;
  minimum_order: number | null;
  delivery_fee_item_id: number | null;
  delivery_fee_item: DeliveryZoneFeeItem | null;
}

/** Pure derivation of the delivery-fee line from a resolved zone. Single
 *  source of truth so the Stripe PaymentIntent amount and the order payload
 *  can never read different fee values for the same submission.
 */
function deriveFee(zone: MatchedZoneSummary | null): {
  feeItem: DeliveryZoneFeeItem | null;
  fee: number;
} {
  const feeItem = zone?.delivery_fee_item ?? null;
  return { feeItem, fee: feeItem ? Number(feeItem.price) : 0 };
}

const CONTACT_STORAGE_KEY = 'hc:contact';

interface PersistedContact {
  name: string;
  address: string;
  phone: string;
  email: string;
  floor: string;
}

function readPersistedContact(): PersistedContact | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONTACT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedContact>;
    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      address: typeof parsed.address === 'string' ? parsed.address : '',
      phone: typeof parsed.phone === 'string' ? parsed.phone : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
      floor: typeof parsed.floor === 'string' ? parsed.floor : '',
    };
  } catch {
    return null;
  }
}

function writePersistedContact(contact: PersistedContact) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONTACT_STORAGE_KEY, JSON.stringify(contact));
  } catch {
    // ignore quota / disabled storage
  }
}

/** Read-only zone polygon rendered on the map */
function ZonePolygonOverlay({ zone }: { zone: DeliveryZone }) {
  const map = useMap();
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    if (!map || !zone.shape_json || zone.shape_json.length < 3) return;

    if (polygonRef.current) {
      polygonRef.current.setMap(null);
    }

    const poly = new google.maps.Polygon({
      paths: zone.shape_json,
      strokeColor: zone.color || '#FF0000',
      strokeWeight: 4,
      fillColor: zone.color || '#FF0000',
      fillOpacity: 0.15,
      clickable: false,
      map,
    });
    polygonRef.current = poly;

    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, [map, zone]);

  return null;
}

/** Keep the checkout map framed on the whole delivery area. Each overlay
 *  used to fitBounds to its own zone, so whichever mounted last won and
 *  the map showed a single zone. This fits the union of all zones — and
 *  once the customer's address geocodes, extends the bounds to keep the
 *  pin in view (relevant when the address is outside every zone). */
function FitZonesAndPin({ zones, pin }: {
  zones: DeliveryZone[];
  pin: { lat: number; lng: number } | null;
}) {
  const map = useMap();

  useEffect(() => {
    if (!map || zones.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    for (const zone of zones) {
      if (zone.shape_json) {
        for (const p of zone.shape_json) bounds.extend(p);
      }
    }
    if (pin) bounds.extend(pin);
    map.fitBounds(bounds, 30);
  }, [map, zones, pin]);

  return null;
}

export function CheckoutForm() {
  const { items, totalPrice, setView, clearCart, setOrderUuid } = useCart();
  const { defaultLocationId, deliveryZones, googleApiKey, onlineStatus } = useMenu();
  const { t, lang } = useI18n();

  const slots = useMemo(() => buildScheduleSlots(onlineStatus?.schedule), [onlineStatus]);
  const unavailable = !!(onlineStatus && (!onlineStatus.is_open || onlineStatus.is_paused));
  // Default to scheduled when restaurant is closed/paused — there's no
  // live confirmation path, so ASAP would just be rejected.
  const [timing, setTiming] = useState<'asap' | 'scheduled'>(unavailable ? 'scheduled' : 'asap');
  const [scheduleDate, setScheduleDate] = useState<string>('');
  const [scheduleTime, setScheduleTime] = useState<string>('');

  // If availability flips after mount, nudge the default once.
  const lastUnavailRef = useRef(unavailable);
  useEffect(() => {
    if (lastUnavailRef.current !== unavailable) {
      lastUnavailRef.current = unavailable;
      if (unavailable && timing === 'asap') setTiming('scheduled');
    }
  }, [unavailable, timing]);

  // Auto-seed date/time once slots become available.
  useEffect(() => {
    if (timing !== 'scheduled') return;
    if (!scheduleDate && slots.dates.length > 0) {
      const first = slots.dates[0];
      setScheduleDate(first);
      const firstSlot = slots.byDate[first]?.[0] ?? '';
      setScheduleTime(firstSlot);
    } else if (scheduleDate && !scheduleTime) {
      const firstSlot = slots.byDate[scheduleDate]?.[0] ?? '';
      setScheduleTime(firstSlot);
    }
  }, [timing, slots, scheduleDate, scheduleTime]);

  // Contact fields are seeded from the last successful order on this
  // device (persisted in handleOrderAccepted) so returning customers
  // don't retype them. The note is order-specific and never persisted.
  const persistedContact = useMemo(readPersistedContact, []);
  const [name, setName] = useState(persistedContact?.name ?? '');
  const [address, setAddress] = useState(persistedContact?.address ?? '');
  const [phone, setPhone] = useState(persistedContact?.phone ?? '');
  const [email, setEmail] = useState(persistedContact?.email ?? '');
  const [floor, setFloor] = useState(persistedContact?.floor ?? '');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Accordion: only one step open at a time. Start with Kontakt expanded.
  const [openStep, setOpenStep] = useState<'contact' | 'timing' | 'payment'>('contact');
  const toggleStep = (s: 'contact' | 'timing' | 'payment') =>
    setOpenStep((cur) => (cur === s ? cur : s));

  // Idempotency key for the in-flight order. Generated lazily on first
  // submitOrderDirect call and reused across Stripe retries / manual retries
  // until the POST returns success; backend dedupes by delivery.uuid so a
  // network blip during Stripe confirm doesn't double-create the order.
  const pendingSubmitUUIDRef = useRef<string | null>(null);

  // Fee item locked in for the current submission attempt. Written once by
  // handleSubmit after the zone is fully resolved (cached or freshly
  // geocoded); read by buildPayload. Because both the Stripe PaymentIntent
  // amount and the order payload's fee line derive from this single value,
  // they can't disagree even if matchedZone state changes between writing
  // and reading. Cleared when navigating back from the Stripe form so the
  // next submit re-resolves.
  const committedFeeItemRef = useRef<DeliveryZoneFeeItem | null>(null);

  // Payment state
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card'>('cash');
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showStripeForm, setShowStripeForm] = useState(false);

  // Zone validation state
  const [addressCoords, setAddressCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [addressOutOfZone, setAddressOutOfZone] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [matchedZone, setMatchedZone] = useState<MatchedZoneSummary | null>(null);

  // Zones with polygon data
  const zonesWithPolygon = deliveryZones.filter(
    (z) => z.shape_json && Array.isArray(z.shape_json) && z.shape_json.length >= 3
  );
  const hasZoneValidation = zonesWithPolygon.length > 0 && !!googleApiKey;

  // Load Stripe publishable key on mount
  useEffect(() => {
    fetchStripeConfig()
      .then((config) => {
        if (config.publishable_key) {
          setStripePromise(loadStripe(config.publishable_key));
        }
      })
      .catch(() => {
        // Stripe not configured — card payments will not be available
      });
  }, []);

  const geocodeAddress = useCallback(async (addr: string) => {
    if (!addr.trim() || !hasZoneValidation) return;

    setGeocoding(true);
    setAddressOutOfZone(false);
    setAddressCoords(null);
    setMatchedZone(null);

    try {
      const result = await checkAddress(addr.trim());
      if (result.lat != null && result.lng != null) {
        setAddressCoords({ lat: result.lat, lng: result.lng });
        setAddressOutOfZone(!result.in_zone);
        setMatchedZone(result.zone ?? null);
      }
    } catch {
      // Geocoding failed — don't block, just skip validation
    } finally {
      setGeocoding(false);
    }
  }, [hasZoneValidation]);

  // If the address was prefilled from a previous visit, run the zone
  // check once as soon as zone validation is available — the pin, fee
  // and minimum-order display then match a hand-typed (blurred) address.
  const prefillGeocodeRan = useRef(false);
  useEffect(() => {
    if (prefillGeocodeRan.current || !hasZoneValidation) return;
    prefillGeocodeRan.current = true;
    if (persistedContact?.address?.trim()) {
      geocodeAddress(persistedContact.address);
    }
  }, [hasZoneValidation, geocodeAddress, persistedContact]);

  function handleAddressChange(value: string) {
    setAddress(value);
    setAddressCoords(null);
    setAddressOutOfZone(false);
    setMatchedZone(null);
  }

  function handleAddressBlur() {
    if (address.trim()) {
      geocodeAddress(address);
    }
  }

  // Display-only derivations from current zone state. The *committed* fee
  // used for the in-flight submission is held in committedFeeItemRef below
  // — that ref is the value PaymentIntent and order payload both consult,
  // so they cannot diverge if state changes mid-submit.
  const { fee: deliveryFee } = deriveFee(matchedZone);
  const minimumOrder = matchedZone?.minimum_order ?? null;
  const grandTotal = totalPrice + deliveryFee;

  function buildDelayedTimeISO(): string | null {
    if (timing !== 'scheduled') return null;
    if (!scheduleDate || !scheduleTime) return null;
    const [y, m, d] = scheduleDate.split('-').map((x) => parseInt(x, 10));
    const [hh, mm] = scheduleTime.split(':').map((x) => parseInt(x, 10));
    // Local-time Date — toISOString() emits UTC, which is what the backend expects.
    return new Date(y, m - 1, d, hh, mm).toISOString();
  }

  function buildPayload(ccPayment: boolean): OrderPayload {
    const orders: OrderPayload['orders'] = [];
    for (const ci of items) {
      for (let i = 0; i < ci.quantity; i++) {
        orders.push({
          id: ci.item.id,
          count: 1,
          addons: ci.addons.map((a) => ({ id: a.id })),
          note: ci.note || undefined,
        });
      }
    }
    // The delivery fee is a real MenuItem configured on the matched zone.
    // Append it as its own order line so it persists on the delivery record
    // and renders on the ticket like any other item. Backend validates that
    // exactly one fee line is present when the zone defines one.
    //
    // Use the *committed* fee item from the ref, not closure state — handleSubmit
    // sets it after resolving the zone, so the order payload's fee line always
    // matches whatever was used to compute the PaymentIntent amount, even if
    // matchedZone state was updated mid-submit and React hadn't re-rendered yet
    // by the time amountCents was computed.
    const committedFee = committedFeeItemRef.current;
    if (committedFee) {
      orders.push({ id: committedFee.id, count: 1, addons: [] });
    }

    const payload: OrderPayload = {
      orders,
      customer_address: {
        name: name.trim(),
        address: address.trim(),
        contact_phone: phone.trim(),
        floor: floor.trim() || undefined,
        extra: note.trim() || undefined,
      },
      email: email.trim(),
      location: { id: defaultLocationId ?? 0 },
      cc_payment: ccPayment,
      online_order: true,
    };
    const delayedIso = buildDelayedTimeISO();
    if (delayedIso) {
      payload.delayed = true;
      payload.delayed_time = delayedIso;
    }
    return payload;
  }

  function validateForm(): boolean {
    if (!name.trim() || !address.trim() || !phone.trim() || !email.trim()) {
      setError(t('checkout.fillRequired'));
      return false;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email.trim())) {
      setError(t('checkout.emailInvalid'));
      return false;
    }
    if (!defaultLocationId) {
      setError(t('checkout.noLocation'));
      return false;
    }
    if (addressOutOfZone) {
      setError('Dostava nije dostupna na vašu adresu.');
      return false;
    }
    if (minimumOrder != null && totalPrice < minimumOrder) {
      setError(`Minimalni iznos narudžbe je ${minimumOrder.toFixed(2)} €. Trenutni iznos: ${totalPrice.toFixed(2)} €.`);
      return false;
    }
    if (timing === 'scheduled' && (!scheduleDate || !scheduleTime)) {
      setError(t('checkout.scheduleRequired'));
      return false;
    }
    return true;
  }

  /** Return the zone that should drive pricing for the current submission.
   *  Either uses what state already holds (when a prior blur-geocode has
   *  populated addressCoords) or runs a fresh geocode now and commits the
   *  result to state. Crucially the returned value is the *resolved* zone —
   *  callers must use it directly rather than reading matchedZone, which
   *  the React render hasn't seen the setState for yet.
   */
  async function ensureZoneResolved(): Promise<{
    zone: MatchedZoneSummary | null;
    outOfZone: boolean;
    geocodeFailed: boolean;
  }> {
    if (!hasZoneValidation) return { zone: null, outOfZone: false, geocodeFailed: false };
    // Cached path — prior blur-geocode populated coords and matched a zone.
    if (addressCoords && !addressOutOfZone) {
      return { zone: matchedZone, outOfZone: false, geocodeFailed: false };
    }
    if (!address.trim()) return { zone: null, outOfZone: false, geocodeFailed: false };

    setGeocoding(true);
    setAddressOutOfZone(false);
    setMatchedZone(null);
    try {
      const result = await checkAddress(address.trim());
      if (result.lat == null || result.lng == null) {
        return { zone: null, outOfZone: false, geocodeFailed: true };
      }
      const outOfZone = !result.in_zone;
      const zone = result.zone ?? null;
      setAddressCoords({ lat: result.lat, lng: result.lng });
      setAddressOutOfZone(outOfZone);
      setMatchedZone(zone);
      return { zone, outOfZone, geocodeFailed: false };
    } catch {
      return { zone: null, outOfZone: false, geocodeFailed: true };
    } finally {
      setGeocoding(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Phase 1: resolve the zone (cached or freshly geocoded). All downstream
    // pricing decisions use the returned `resolvedZone` directly — never the
    // matchedZone closure, which is one render behind any setState above.
    const { zone: resolvedZone, outOfZone } = await ensureZoneResolved();
    if (outOfZone) {
      setError('Dostava nije dostupna na vašu adresu.');
      return;
    }

    // Phase 2: derive the fee/total once, from the resolved zone. The same
    // values are used to build the PaymentIntent amount and (via the ref) the
    // order payload, so the two cannot disagree.
    const { feeItem: resolvedFeeItem, fee: resolvedFee } = deriveFee(resolvedZone);
    const resolvedGrandTotal = totalPrice + resolvedFee;
    const resolvedMinimum = resolvedZone?.minimum_order ?? null;

    if (resolvedMinimum != null && totalPrice < resolvedMinimum) {
      setError(`Minimalni iznos narudžbe je ${resolvedMinimum.toFixed(2)} €. Trenutni iznos: ${totalPrice.toFixed(2)} €.`);
      return;
    }

    // Re-check availability before submitting — but only for ASAP orders.
    // Scheduled (delayed) orders are explicitly for a future slot and the
    // backend bypasses the open/paused gate for them.
    if (timing === 'asap') {
      try {
        const status = await fetchOnlineStatus();
        if (status.is_paused) {
          setError('Online narudžbe su trenutno pauzirane. Pokušajte ponovo za nekoliko minuta.');
          return;
        }
        if (!status.is_open) {
          setError('Online narudžbe trenutno nije dostupne. Provjerite radno vrijeme.');
          return;
        }
      } catch {
        // If the check fails, proceed anyway — backend validates too
      }
    }

    if (!validateForm()) return;

    // Phase 3: commit the fee item as the value of record for this attempt.
    // buildPayload reads from this ref, so even if the Stripe roundtrip
    // re-renders the component with different matchedZone state, the order
    // POSTed at the end will carry the same fee line that was charged.
    committedFeeItemRef.current = resolvedFeeItem;

    if (paymentMethod === 'card' && stripePromise) {
      setSubmitting(true);
      try {
        const amountCents = Math.round(resolvedGrandTotal * 100);
        const { client_secret } = await createPaymentIntent(amountCents);
        setClientSecret(client_secret);
        setShowStripeForm(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize payment.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Cash payment — submit directly
    await submitOrderDirect(false);
  }

  async function submitOrderDirect(ccPayment: boolean, onlinePaid = false, paymentIntentId?: string) {
    setSubmitting(true);
    try {
      if (!pendingSubmitUUIDRef.current) {
        pendingSubmitUUIDRef.current = crypto.randomUUID();
      }
      const payload = buildPayload(ccPayment);
      payload.uuid = pendingSubmitUUIDRef.current;
      if (onlinePaid) payload.online_paid = true;
      // The backend verifies this id against Stripe to set paid status; the
      // online_paid flag alone is not trusted server-side.
      if (paymentIntentId) payload.payment_intent_id = paymentIntentId;
      const result = await submitOrder(payload);
      setOrderUuid(result.uuid);
      pendingSubmitUUIDRef.current = null;
      // Order accepted — remember the contact details for the next visit.
      writePersistedContact({ name, address, phone, email, floor });
      clearCart();
      setView('waiting');
    } catch (err) {
      // Leave pendingSubmitUUIDRef set so a manual retry reuses the key
      // and the backend dedupes if the first POST actually landed.
      setError(err instanceof Error ? err.message : 'Order submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStripeSuccess(paymentIntentId: string) {
    // Payment confirmed via Stripe — online_paid only, not cc_payment. Pass the
    // PaymentIntent id so the backend can verify the charge server-side.
    await submitOrderDirect(false, true, paymentIntentId);
  }

  function leaveStripeForm() {
    // Drop the committed fee so the next submit re-resolves the zone and
    // re-commits — covers the case where the user backs out, edits the
    // address (potentially landing in a different zone), and resubmits.
    committedFeeItemRef.current = null;
    setShowStripeForm(false);
  }

  // Show Stripe payment form
  if (showStripeForm && clientSecret && stripePromise) {
    return (
      <div className="hc-checkout">
        <div className="hc-checkout-header">
          <button className="hc-back-btn" onClick={leaveStripeForm}>
            &larr; {t('checkout.back')}
          </button>
          <h2>{t('checkout.paymentCard')}</h2>
        </div>
        <div className="hc-checkout-form">
          <div className="hc-checkout-summary">
            <div className="hc-summary-line">
              <span>{t('checkout.items')}</span>
              <span>{totalPrice.toFixed(2)} &euro;</span>
            </div>
            {deliveryFee > 0 && (
              <div className="hc-summary-line">
                <span>{t('checkout.deliveryFee')}</span>
                <span>{deliveryFee.toFixed(2)} &euro;</span>
              </div>
            )}
            {matchedZone && deliveryFee === 0 && (
              <div className="hc-summary-line">
                <span>{t('checkout.deliveryFee')}</span>
                <span className="hc-summary-free">{t('checkout.free')}</span>
              </div>
            )}
            <div className="hc-summary-line hc-summary-total">
              <span>{t('checkout.total')}</span>
              <strong>{grandTotal.toFixed(2)} &euro;</strong>
            </div>
          </div>
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              onSuccess={handleStripeSuccess}
              onCancel={leaveStripeForm}
            />
          </Elements>
          {error && <div className="hc-form-error">{error}</div>}
        </div>
      </div>
    );
  }

  const timingSummary = timing === 'asap'
    ? t('checkout.timingAsap')
    : (scheduleDate && scheduleTime ? `${formatDateLabel(scheduleDate, lang)} · ${scheduleTime}` : t('checkout.timingScheduled'));
  const paymentSummary = paymentMethod === 'cash' ? t('checkout.paymentCash') : t('checkout.paymentCard');

  return (
    <div className="hc-checkout">
      <div className="hc-checkout-header">
        <button className="hc-back-btn" onClick={() => setView('cart')}>
          &larr; {t('checkout.back')}
        </button>
        <h2>{t('checkout.title')}</h2>
      </div>

      <form className="hc-checkout-form" onSubmit={handleSubmit} id="hc-checkout-form">
        {/* Step 1 — Kontakt */}
        <section className={`hc-step ${openStep === 'contact' ? 'hc-step-open' : ''}`}>
          <button
            type="button"
            className="hc-step-header"
            onClick={() => toggleStep('contact')}
            aria-expanded={openStep === 'contact'}
          >
            <span className="hc-step-number">1</span>
            <span className="hc-step-title">{t('step.contact')}</span>
            <span className="hc-step-summary">
              {name.trim() ? name.trim() : ''}
            </span>
            <span className="hc-step-chevron">&#9662;</span>
          </button>
          <div className="hc-step-body">
            <div className={`hc-contact-grid ${hasZoneValidation ? 'hc-has-map' : ''}`}>
              <div className="hc-contact-fields">
                <div className="hc-form-group">
                  <label htmlFor="hc-name">{t('checkout.name')} *</label>
                  <input
                    id="hc-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('checkout.name')}
                    maxLength={255}
                    required
                  />
                </div>

                <div className="hc-form-group">
                  <label htmlFor="hc-address">{t('checkout.address')} *</label>
                  <input
                    id="hc-address"
                    type="text"
                    value={address}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    onBlur={handleAddressBlur}
                    placeholder={t('checkout.address')}
                    maxLength={255}
                    required
                  />
                  {geocoding && (
                    <div className="hc-zone-checking">Provjeravamo adresu...</div>
                  )}
                  {addressOutOfZone && !geocoding && (
                    <div className="hc-zone-warning">Dostava nije dostupna u vašem području</div>
                  )}
                </div>

                <div className="hc-form-group">
                  <label htmlFor="hc-floor">{t('checkout.floor')}</label>
                  <input
                    id="hc-floor"
                    type="text"
                    value={floor}
                    onChange={(e) => setFloor(e.target.value)}
                    placeholder={t('checkout.floor')}
                    maxLength={255}
                  />
                </div>

                <div className="hc-form-group">
                  <label htmlFor="hc-phone">{t('checkout.phone')} *</label>
                  <input
                    id="hc-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+385..."
                    maxLength={20}
                    required
                  />
                </div>

                <div className="hc-form-group">
                  <label htmlFor="hc-email">{t('checkout.email')} *</label>
                  <input
                    id="hc-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vasa@mail.hr"
                    maxLength={255}
                    required
                  />
                </div>
              </div>

              {hasZoneValidation && (
                <div className="hc-zone-map-container">
                  <APIProvider apiKey={googleApiKey!}>
                    <Map
                      defaultCenter={
                        (zonesWithPolygon[0]?.shape_json?.[0]) ||
                        { lat: 45.8, lng: 15.97 }
                      }
                      defaultZoom={13}
                      mapId="checkout-zone-map"
                      style={{ width: '100%', height: '100%' }}
                      gestureHandling="cooperative"
                      disableDefaultUI
                      zoomControl
                    >
                      {zonesWithPolygon.map((zone) => (
                        <ZonePolygonOverlay key={zone.id} zone={zone} />
                      ))}
                      <FitZonesAndPin zones={zonesWithPolygon} pin={addressCoords} />
                      {addressCoords && (
                        <AdvancedMarker position={addressCoords} />
                      )}
                    </Map>
                  </APIProvider>
                </div>
              )}
            </div>

            <div className="hc-form-group">
              <label htmlFor="hc-note">{t('checkout.note')}</label>
              <textarea
                id="hc-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('checkout.note')}
                rows={3}
                maxLength={255}
              />
            </div>

            <button
              type="button"
              className="hc-step-next"
              onClick={() => toggleStep('timing')}
            >
              {t('step.next')} &rarr;
            </button>
          </div>
        </section>

        {/* Step 2 — Vrijeme */}
        <section className={`hc-step ${openStep === 'timing' ? 'hc-step-open' : ''}`}>
          <button
            type="button"
            className="hc-step-header"
            onClick={() => toggleStep('timing')}
            aria-expanded={openStep === 'timing'}
          >
            <span className="hc-step-number">2</span>
            <span className="hc-step-title">{t('step.timing')}</span>
            <span className="hc-step-summary">{timingSummary}</span>
            <span className="hc-step-chevron">&#9662;</span>
          </button>
          <div className="hc-step-body">
            <div className="hc-timing-options">
              <label className={`hc-timing-option ${timing === 'asap' ? 'hc-timing-active' : ''} ${unavailable ? 'hc-timing-disabled' : ''}`}>
                <input
                  type="radio"
                  name="hc-timing"
                  value="asap"
                  checked={timing === 'asap'}
                  onChange={() => setTiming('asap')}
                  disabled={unavailable}
                />
                <span>{t('checkout.timingAsap')}</span>
              </label>
              <label className={`hc-timing-option ${timing === 'scheduled' ? 'hc-timing-active' : ''}`}>
                <input
                  type="radio"
                  name="hc-timing"
                  value="scheduled"
                  checked={timing === 'scheduled'}
                  onChange={() => setTiming('scheduled')}
                />
                <span>{t('checkout.timingScheduled')}</span>
              </label>
            </div>
            {timing === 'scheduled' && (
              slots.dates.length === 0 ? (
                <div className="hc-zone-warning" style={{ marginTop: 12 }}>{t('checkout.scheduleNoDays')}</div>
              ) : (
                <>
                  <div className="hc-schedule-row">
                    <div className="hc-schedule-field">
                      <label htmlFor="hc-schedule-date">{t('checkout.scheduleDate')}</label>
                      <select
                        id="hc-schedule-date"
                        value={scheduleDate}
                        onChange={(e) => {
                          setScheduleDate(e.target.value);
                          const first = slots.byDate[e.target.value]?.[0] ?? '';
                          setScheduleTime(first);
                        }}
                      >
                        {slots.dates.map((d) => (
                          <option key={d} value={d}>{formatDateLabel(d, lang)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="hc-schedule-field">
                      <label htmlFor="hc-schedule-time">{t('checkout.scheduleTime')}</label>
                      <select
                        id="hc-schedule-time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                      >
                        {(slots.byDate[scheduleDate] ?? []).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="hc-schedule-hint">{t('checkout.scheduleHint')}</div>
                </>
              )
            )}
            <button
              type="button"
              className="hc-step-next"
              onClick={() => toggleStep('payment')}
            >
              {t('step.next')} &rarr;
            </button>
          </div>
        </section>

        {/* Step 3 — Plaćanje */}
        {stripePromise && (
          <section className={`hc-step ${openStep === 'payment' ? 'hc-step-open' : ''}`}>
            <button
              type="button"
              className="hc-step-header"
              onClick={() => toggleStep('payment')}
              aria-expanded={openStep === 'payment'}
            >
              <span className="hc-step-number">3</span>
              <span className="hc-step-title">{t('step.payment')}</span>
              <span className="hc-step-summary">{paymentSummary}</span>
              <span className="hc-step-chevron">&#9662;</span>
            </button>
            <div className="hc-step-body">
              <PaymentMethodPicker selected={paymentMethod} onChange={setPaymentMethod} />
            </div>
          </section>
        )}

        {/* Always-visible summary */}
        <div className="hc-checkout-summary">
          <div className="hc-summary-line">
            <span>{t('checkout.items')}</span>
            <span>{totalPrice.toFixed(2)} &euro;</span>
          </div>
          {deliveryFee > 0 && (
            <div className="hc-summary-line">
              <span>{t('checkout.deliveryFee')}</span>
              <span>{deliveryFee.toFixed(2)} &euro;</span>
            </div>
          )}
          {matchedZone && deliveryFee === 0 && (
            <div className="hc-summary-line">
              <span>{t('checkout.deliveryFee')}</span>
              <span className="hc-summary-free">{t('checkout.free')}</span>
            </div>
          )}
          <div className="hc-summary-line hc-summary-total">
            <span>{t('checkout.total')}</span>
            <strong>{grandTotal.toFixed(2)} &euro;</strong>
          </div>
          {minimumOrder != null && totalPrice < minimumOrder && (
            <div className="hc-zone-warning" style={{ marginTop: 8 }}>
              Minimalni iznos narudžbe je {minimumOrder.toFixed(2)} &euro;
            </div>
          )}
        </div>

        {error && <div className="hc-form-error">{error}</div>}
      </form>

      {/* Sticky bottom CTA bar */}
      <div className="hc-checkout-cta-bar">
        <div className="hc-checkout-cta-total">
          <span className="hc-checkout-cta-total-label">{t('checkout.total')}</span>
          <span className="hc-checkout-cta-total-value">{grandTotal.toFixed(2)} &euro;</span>
        </div>
        <button
          type="submit"
          form="hc-checkout-form"
          className="hc-btn hc-btn-primary hc-btn-block"
          disabled={submitting}
        >
          {submitting ? t('checkout.submitting') : t('checkout.submit')}
        </button>
      </div>
    </div>
  );
}
